<template>
  <SearchableSelect
    v-model="selectedScopesComputed"
    :items="scopeItems"
    return-object
    :loading="loading"
    :label="t(selectedViewBy)"
    :multiple="!singleSelect"
    :customToggleAllFn="toggleAll"
    :itemTitle="itemTitle"
    :rules="rules"
    :showSelectedAtTop="showSelectedAtTop"
  />
</template>

<script setup>
import { computed, watch, toRefs } from 'vue';
import { useRootStore } from '../../../../stores/root';
import { storeToRefs } from 'pinia';
import SearchableSelect from '../../../SearchableSelect.vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = defineProps({
  selectedViewBy: {
    type: String,
  },
  selectedScopes: {
    type: Array,
  },
  singleSelect: {
    type: Boolean,
    default: false,
  },
  onlyActiveLocations: {
    type: Boolean,
    default: true,
  },
  maxLocations: {
    type: Number,
  },
  showSelectedAtTop: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(['update:selectedScopes']);
const {
  selectedScopes,
  selectedViewBy,
  singleSelect,
  maxLocations,
  onlyActiveLocations,
  showSelectedAtTop,
} = toRefs(props);

const store = useRootStore();
const { patches, locations, hierarchyByUser } = storeToRefs(store);

const loading = computed(() => {
  if (selectedViewBy.value === 'patches' && !patches.value) return true;
  return false;
});

const selectedScopesComputed = computed({
  get() {
    return selectedScopes.value;
  },
  set: (v) => {
    emit('update:selectedScopes', v);
  },
});

const filteredLocations = computed(() => {
  if (!locations.value) return [];
  const mappedLocations = [...locations.value].sort(
    (a, b) => a.value - b.value,
  );
  if (!onlyActiveLocations.value) {
    return mappedLocations;
  }
  return mappedLocations.filter((loc) => loc.isActive);
});

const filteredPatches = computed(() => {
  if (!patches.value) return [];
  if (!onlyActiveLocations.value) return patches.value;
  return patches.value.reduce((acc, patch) => {
    const newPatch = {
      ...patch,
      locations: patch.locations.filter((nsn) =>
        filteredLocations.value.find((loc) => loc.value === nsn),
      ),
    };
    if (newPatch.locations.length) acc.push(newPatch);
    return acc;
  }, []);
});

const scopeItems = computed(() => {
  if (!selectedViewBy.value) return [];
  if (selectedViewBy.value === 'locations') return filteredLocations.value;
  if (selectedViewBy.value === 'patches') return filteredPatches.value;

  // hierarchy
  const selectedHierarchy = hierarchyByUser.value?.find(
    (role) => role.text === selectedViewBy.value,
  );
  if (selectedHierarchy) {
    const users = selectedHierarchy.users.reduce((acc, user) => {
      const userFilteredLocations = [];
      const userLocations = user.locations.reduce((acc, loc) => {
        const location = filteredLocations.value.find(
          (location) => location.value === loc,
        );
        if (location) {
          acc.push(loc);
        } else {
          userFilteredLocations.push(loc);
        }
        return acc;
      }, []);
      if (user.label) {
        acc.push({
          name: user.label,
          locations: userLocations,
          isFiltered: userFilteredLocations.length > 0,
          filteredLocations: userFilteredLocations,
        });
      }
      return acc;
    }, []);
    return users;
  }
  return [];
});

const itemTitle = computed(() => {
  if (selectedViewBy.value === 'locations') return 'nameValue';
  return 'name';
});

function toggleAllLocations() {
  if (singleSelect.value) {
    selectedScopesComputed.value =
      scopeItems.value.find((x) => x.isActive) || scopeItems.value[0];
    return;
  }
  const activeLocations = scopeItems.value.filter((x) => x.isActive);
  if (selectedScopesComputed.value.length === activeLocations.length) {
    selectedScopesComputed.value = [];
    return;
  }
  if (!maxLocations.value) {
    selectedScopesComputed.value = scopeItems.value.filter((x) => x.isActive);
    return;
  }

  selectedScopesComputed.value = scopeItems.value
    .filter((x) => x.isActive)
    .slice(0, maxLocations.value);
}

function toggleAllPatches() {
  // patches can never be shown when singleSelect is true
  if (!maxLocations.value) {
    if (selectedScopesComputed.value.length === scopeItems.value.length) {
      selectedScopesComputed.value = [];
    } else {
      selectedScopesComputed.value = scopeItems.value;
    }
  } else {
    if (selectedScopesComputed.value.length) {
      selectedScopesComputed.value = [];
      return;
    }
    selectedScopesComputed.value = scopeItems.value.reduce((scopes, scope) => {
      const currentScopes = scopes.reduce((prev, curr) => {
        prev.push(...curr.value);
        return prev;
      }, []);
      const scopeSet = new Set(currentScopes);
      if (maxLocations.value > scopeSet.size + scope.value.length) {
        scopes.push(scope);
      }
      return scopes;
    }, []);
  }
}

function toggleAll(force = false) {
  if (selectedViewBy.value === 'locations') {
    toggleAllLocations(force);
  } else {
    toggleAllPatches(force);
  }
}

watch(selectedViewBy, () => {
  if (selectedViewBy.value === 'locations') toggleAll(true);
  else selectedScopesComputed.value = [];
});

const rules = [
  (v) => {
    if (singleSelect.value && !v) {
      return t('please-select-a-location');
    }
    if (!singleSelect.value && !v.length) {
      return t('please-select-at-least-one-location');
    }
    return true;
  },
];
</script>
<i18n>
{
  "en": {
    "locations": "Locations",
    "patches": "Patches",
    "please-select-a-location": "Please select a location",
    "please-select-at-least-one-location": "Please select at least one location",
  },
  "es": {
    "locations": "Ubicaciones",
    "patches": "Grupos",
    "please-select-a-location": "Por favor selecciona una ubicación",
    "please-select-at-least-one-location": "Por favor selecciona al menos una ubicación",
  }
}
</i18n>
<style scoped>
.no-wrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
:deep(.v-input__details) {
  display: none;
}
</style>
